<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Page Header (matching create page) -->
      <v-row class="mb-1">
        <v-col cols="12">
          <v-card elevation="2" class="page-header-card">
            <v-card-title class="py-1 px-2">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Edit Bank Detail</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Modify bank account information and settings
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Edit Toggle and Back Button -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Edit/Read-Only Toggle Button -->
                    <v-btn
                      :color="isEditMode ? 'warning' : 'primary'"
                      :outlined="!isEditMode"
                      @click="toggleEditMode"
                      class="mr-2"
                      small
                    >
                      <v-icon left>{{
                        isEditMode ? "mdi-eye" : "mdi-pencil"
                      }}</v-icon>
                      {{ isEditMode ? "Read Only" : "Edit Mode" }}
                    </v-btn>

                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                      small
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Bank Details
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Form Header -->
            <v-card-title class="form-header py-1 px-2">
              <h2 class="form-title">Bank Account Information</h2>
              <v-spacer></v-spacer>
              <v-chip
                :color="isEditMode ? 'success' : 'warning'"
                :outlined="!isEditMode"
                x-small
              >
                <v-icon left x-small>
                  {{ isEditMode ? "mdi-pencil" : "mdi-eye" }}
                </v-icon>
                {{ isEditMode ? "Edit Mode" : "Read Only Mode" }}
              </v-chip>
            </v-card-title>

            <v-divider></v-divider>
            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Account Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2"
                      >mdi-account-details</v-icon
                    >
                    Account Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.account_name"
                        label="Account Holder Name *"
                        placeholder="Enter account holder name"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[
                          (v) => !!v || 'Account holder name is required',
                        ]"
                        :error-messages="errors.account_name"
                        @input="clearError('account_name')"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.account_number"
                        label="Account Number *"
                        placeholder="Enter account number"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[
                          (v) => !!v || 'Account number is required',
                          (v) =>
                            /^[0-9]+$/.test(v) ||
                            'Account number must contain only numbers',
                        ]"
                        :error-messages="errors.account_number"
                        @input="clearError('account_number')"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-credit-card</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.user_id"
                        :items="users"
                        label="User (Optional)"
                        placeholder="Select user"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.user_id"
                        @input="clearError('user_id')"
                        clearable
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.account_type_id"
                        :items="accountTypes"
                        label="Account Type (Optional)"
                        placeholder="Select account type"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.account_type_id"
                        @input="clearError('account_type_id')"
                        clearable
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-bank</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                </div>

                <!-- Bank Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-bank</v-icon>
                    Bank Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.bank_name"
                        :items="bankNames"
                        label="Bank Name *"
                        placeholder="Select or enter bank name"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'Bank name is required']"
                        :error-messages="errors.bank_name"
                        @input="clearError('bank_name')"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-bank</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.branch"
                        label="Branch"
                        placeholder="Enter branch name/location"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.branch"
                        @input="clearError('branch')"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-bank-outline</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.ifsc_code"
                        label="IFSC Code"
                        placeholder="e.g., SBIN0001234"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[
                          (v) =>
                            !v ||
                            /^[A-Z]{4}0[A-Z0-9]{6}$/.test(v) ||
                            'Invalid IFSC format (e.g., SBIN0001234)',
                        ]"
                        :error-messages="errors.ifsc_code"
                        @input="clearError('ifsc_code')"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-code-string</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.micr_code"
                        label="MICR Code"
                        placeholder="Enter 9-digit MICR code"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[
                          (v) =>
                            !v ||
                            /^[0-9]{9}$/.test(v) ||
                            'MICR code must be exactly 9 digits',
                        ]"
                        :error-messages="errors.micr_code"
                        @input="clearError('micr_code')"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-numeric</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Status Settings Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2"
                      >mdi-toggle-switch</v-icon
                    >
                    Status Settings
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-card outlined class="status-card">
                        <v-card-text class="pa-2">
                          <div class="d-flex align-center">
                            <v-icon color="grey" class="mr-3"
                              >mdi-bank-check</v-icon
                            >
                            <div class="flex-grow-1">
                              <div class="font-weight-medium mb-1">
                                Account Status
                              </div>
                              <div class="text-caption grey--text">
                                Set the status of this bank account
                              </div>
                            </div>
                            <v-switch
                              v-model="form.status"
                              color="success"
                              inset
                              hide-details
                              :disabled="!isEditMode"
                              class="ml-3"
                            ></v-switch>
                          </div>
                          <div class="mt-2">
                            <v-chip
                              small
                              :color="form.status ? 'success' : 'warning'"
                              :text-color="form.status ? 'white' : 'black'"
                            >
                              {{
                                form.status
                                  ? "Active Account"
                                  : "Inactive Account"
                              }}
                            </v-chip>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-card outlined class="status-card">
                        <v-card-text class="pa-2">
                          <div class="d-flex align-center">
                            <v-icon color="grey" class="mr-3">mdi-eye</v-icon>
                            <div class="flex-grow-1">
                              <div class="font-weight-medium mb-1">
                                Visibility Status
                              </div>
                              <div class="text-caption grey--text">
                                Control visibility of this record
                              </div>
                            </div>
                            <v-switch
                              v-model="form.is_active"
                              color="primary"
                              inset
                              hide-details
                              :disabled="!isEditMode"
                              class="ml-3"
                            ></v-switch>
                          </div>
                          <div class="mt-2">
                            <v-chip
                              small
                              :color="form.is_active ? 'primary' : 'grey'"
                              :text-color="form.is_active ? 'white' : 'white'"
                            >
                              {{ form.is_active ? "Visible" : "Hidden" }}
                            </v-chip>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-form>
            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip
                      small
                      :color="hasValidationErrors() ? 'error' : 'success'"
                      :text-color="hasValidationErrors() ? 'white' : 'white'"
                      class="mr-3"
                    >
                      <v-icon left x-small>
                        {{
                          hasValidationErrors()
                            ? "mdi-alert-circle"
                            : "mdi-check-circle"
                        }}
                      </v-icon>
                      {{
                        hasValidationErrors()
                          ? "Please fix validation errors"
                          : "Form validation passed"
                      }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      All required fields must be completed
                    </span>
                  </div>
                </v-col>

                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn
                      color="grey"
                      outlined
                      class="mr-3 action-button"
                      @click="$router.back()"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="save"
                      :loading="isLoading"
                      :disabled="!isEditMode || isLoading"
                      v-show="isEditMode"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Update Bank Detail
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from 'axios';

export default {
  name: "BankDetailEdit",
  data() {
    return {
      valid: false,
      form: {
        user_id: null,
        account_name: "",
        account_number: "",
        bank_name: "",
        branch: "",
        ifsc_code: "",
        micr_code: "",
        account_type_id: null,
        status: false,
        is_active: true,
      },
      errors: {},
      isLoading: false,
      isEditMode: false, // Toggle between edit and read-only mode

      // Form options
      users: [],
      accountTypes: [],
      bankNames: [
        { text: "State Bank of India", value: "State Bank of India" },
        { text: "HDFC Bank", value: "HDFC Bank" },
        { text: "ICICI Bank", value: "ICICI Bank" },
        { text: "Axis Bank", value: "Axis Bank" },
        { text: "Punjab National Bank", value: "Punjab National Bank" },
        { text: "Bank of Baroda", value: "Bank of Baroda" },
        { text: "Canara Bank", value: "Canara Bank" },
        { text: "Union Bank of India", value: "Union Bank of India" },
        { text: "Indian Bank", value: "Indian Bank" },
        { text: "Central Bank of India", value: "Central Bank of India" },
        { text: "IDBI Bank", value: "IDBI Bank" },
        { text: "Indian Overseas Bank", value: "Indian Overseas Bank" },
      ],
    };
  },

  mounted() {
    this.getUsers();
    this.getAccountTypes();
    this.getData();
  },

  methods: {
    hasValidationErrors() {
      return Object.keys(this.errors).length > 0 || !this.valid;
    },

    toggleEditMode() {
      this.isEditMode = !this.isEditMode;
      // Show a message to the user about the mode change
      if (this.isEditMode) {
        this.$toast.success("Edit mode enabled. You can now modify the form.");
      } else {
        this.$toast.info(
          "Read-only mode enabled. Form is now protected from changes."
        );
      }
    },

    async getData() {
      this.isLoading = true;
      const bankDetailId = this.$route.params.id;

      try {
        const response = await axios.get(`/bank_details/${bankDetailId}`);

        if (response.data.success) {
          this.form = response.data.data;
          console.log("Bank Detail loaded:", this.form);
        } else {
          this.$store.dispatch("errors/setError", {
            message: response.data.message || "Failed to load bank detail",
          });
        }
      } catch (error) {
        console.error("Error fetching bank detail:", error);
        if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Error loading bank detail data",
          });
        }
      } finally {
        this.isLoading = false;
      }
    },

    async getUsers() {
      try {
        const response = await axios.get("/users", {
          params: { is_active: 1, show_deleted: 0, rowsPerPage: -1 },
        });
        if (response.data.success) {
          this.users = response.data.data.map((user) => ({
            text: `${user.first_name} ${user.last_name} (${user.email})`,
            value: user.id,
          }));
        }
      } catch (error) {
        console.log("Error loading users:", error);
      }
    },

    async getAccountTypes() {
      try {
        const masters = await axios.get("/masters", {
          params: { context: "bank_details" }
        });
        
        if (masters.data.account_types) {
          this.accountTypes = masters.data.account_types.map((type) => ({
            text: type.description,
            value: type.id,
          }));
        }
      } catch (error) {
        console.log("Error loading account types:", error);
        // Fallback to basic account types
        this.accountTypes = [
          { text: "Savings Account", value: 1 },
          { text: "Current Account", value: 2 },
          { text: "Fixed Deposit", value: 3 },
          { text: "Recurring Deposit", value: 4 },
        ];
      }
    },

    clearError(field) {
      if (this.errors[field]) {
        this.$delete(this.errors, field);
      }
    },

    async save() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.isLoading = true;
      this.errors = {};

      try {
        const response = await axios.patch(
          `/bank_details/${this.$route.params.id}`,
          this.form
        );

        if (response.data.success) {
          this.$toast.success("Bank detail updated successfully");
          this.$router.push("/bank-details");
        } else {
          this.$store.dispatch("errors/setError", {
            message: response.data.message || "Failed to update bank detail",
          });
        }
      } catch (error) {
        console.error("Error updating bank detail:", error);

        if (error.response) {
          if (error.response.status === 422) {
            this.errors = error.response.data.errors || {};
          } else if (error.response.status === 401) {
            this.$router.push("/login");
          } else {
            this.$store.dispatch("errors/setError", {
              message:
                error.response.data.message || "Error updating bank detail",
            });
          }
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              "Network error. Please check your connection and try again.",
          });
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Professional Page Header */
.page-header-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-left: 4px solid var(--v-primary-base);
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

/* Professional Form Card */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
}

.form-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.form-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--v-primary-base);
  margin: 0;
}

.form-content {
  background: #ffffff;
}

/* Section Styling */
.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Professional Form Fields */
.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

/* Status Card */
.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Spacing and Alignment */
.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }

  .form-title {
    font-size: 0.9rem;
  }

  .section-title {
    font-size: 0.85rem;
  }

  .form-content {
    padding: 0.75rem !important;
  }

  .back-button,
  .action-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .v-row > .v-col {
    padding: 0 4px;
  }

  .form-content {
    padding: 0.5rem !important;
  }

  .section-title {
    font-size: 0.8rem;
  }

  .professional-field >>> .v-input__control {
    min-height: 36px;
  }

  .professional-field >>> .v-text-field input {
    font-size: 0.7rem;
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .back-button,
  .action-button {
    min-width: 70px;
    height: 28px;
    font-size: 0.65rem;
  }
}

/* Clean Focus States */
.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Consistent Margins */
.mb-1 {
  margin-bottom: 0.5rem !important;
}

.mb-2 {
  margin-bottom: 0.75rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

/* Read-Only Mode Styling */
.professional-field >>> .v-input--is-disabled {
  opacity: 0.7;
}

.professional-field >>> .v-input--is-disabled .v-input__slot {
  background-color: #f5f5f5 !important;
  border-color: #e0e0e0 !important;
}

.professional-field >>> .v-input--is-disabled .v-label {
  color: #757575 !important;
}

.professional-field >>> .v-input--is-disabled input {
  color: #616161 !important;
}

/* Professional Transitions */
.v-card,
.v-btn,
.professional-field,
.status-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>